import type { DashboardData, Owner, Transaction } from "../types";
import type { PriceObservation } from "../types/receipt";
import type { View } from "./views";
import { lazyScreen } from "./lazy-screen";
import NeedsConnection from "./NeedsConnection";
import ScreenErrorBoundary from "./ScreenErrorBoundary";
import { SecondaryBackContext } from "./use-secondary-back";

// Offline-viewable More screens ship in the main bundle so opening them never depends on a
// separate chunk fetch (the failure that looked like “Something went wrong” in airplane mode).
import type { Need } from "../lib/coverage";
import BudgetSettings from "../features/budgets/BudgetSettings";
import AccountSettings from "../features/accounts/AccountSettings";
import ProfileSettings from "../features/profiles/ProfileSettings";
import Payees from "../features/payees/Payees";
import IOUs from "../features/loans/IOUs";
import Bills from "../features/bills/Bills";
import Achievements from "../features/achievements/Achievements";
import CategorySettings from "../features/categories/CategorySettings";
import Trips from "../features/trips/Trips";
import Goals from "../features/goals/Goals";

// These need the API to show anything useful, or are Settings-only health screens — keep lazy,
// and fall back to NeedsConnection if the chunk is missing offline.
const PriceHistory = lazyScreen(() => import("../features/prices/PriceHistory"));
const ErrorLogs = lazyScreen(() => import("../features/settings/ErrorLogs"));
const AiSettings = lazyScreen(() => import("../features/settings/AiSettings"));
const StagingData = lazyScreen(() => import("../features/settings/StagingData"));
const Backups = lazyScreen(() => import("../features/settings/Backups"));
const Receipts = lazyScreen(() => import("../features/receipts/Receipts"));
const SmartRules = lazyScreen(() => import("../features/settings/SmartRules"));

/** The menu each of these screens is reached from, and so where its back link goes. */
const backFrom: Partial<Record<View, "more" | "settings">> = {
  achievements: "more", prices: "more", "account-settings": "more", payees: "more",
  ious: "more", bills: "more", receipts: "more", categories: "more", budgets: "more",
  trips: "more", goals: "more",
  "profile-settings": "settings", ai: "settings", backups: "settings", errors: "settings", "staging-data": "settings",
  "smart-rules": "settings",
};

/** Screens that cannot show useful content without the network. */
const onlineOnlyViews = new Set<View>(["prices", "receipts", "profile-settings", "ai", "backups", "errors", "staging-data", "smart-rules"]);

/** Whichever of the screens reached from More or Settings is open, if any. */
export default function SecondaryScreens({
  view, data, profile, today, nav, refresh, ensure, pin, priceRows, setPriceRows, setDetail,
  iouFocus, setIouFocus, billFocus, setBillFocus, tripFocus, setTripFocus, goalFocus, setGoalFocus, setSettingsDirty, onCategoriesSaved,
  onOpenReconciliation, onStartCategorizeReview, onSwitchProfile, onAddTransaction, online = true,
}: {
  view: View;
  data: DashboardData;
  profile: Owner;
  today: string;
  nav: (view: View) => void;
  refresh: (force?: boolean) => Promise<unknown>;
  /** Makes sure the ledger is loaded back at least as far as `need`. */
  ensure: (need: Need) => Promise<DashboardData | undefined>;
  /** Loads one entry whatever its date. */
  pin: (ids: string[]) => Promise<DashboardData | undefined>;
  priceRows: PriceObservation[];
  setPriceRows: (rows: PriceObservation[]) => void;
  setDetail: (transaction: Transaction | null) => void;
  /** A loan to open on the loans screen, set when arriving from a payee or transaction. */
  iouFocus: string | null;
  setIouFocus: (id: string | null) => void;
  /** A bill to open on the bills screen, set when arriving from one of its transactions. */
  billFocus: string | null;
  setBillFocus: (id: string | null) => void;
  /** A trip to open on the trips screen, set when arriving from the overview active trip card. */
  tripFocus?: string | null;
  setTripFocus?: (id: string | null) => void;
  /** A goal to open on the goals screen, set when arriving from an account card or detail. */
  goalFocus?: string | null;
  setGoalFocus?: (id: string | null) => void;
  setSettingsDirty: (dirty: boolean) => void;
  onCategoriesSaved: (entryOptions: DashboardData["entryOptions"]) => void;
  onOpenReconciliation: () => void;
  onStartCategorizeReview: () => void;
  onSwitchProfile: (name: Owner) => Promise<unknown>;
  onAddTransaction?: (prefill: {
    tripId?: string;
    tripDestination?: string;
    type?: "Expense" | "Income" | "Transfer";
    accountId?: string;
    toAccountId?: string;
    amount?: string;
    exchangeRate?: string;
    notes?: string;
  }) => void;
  online?: boolean;
}) {
  const back = backFrom[view];
  const onBack = back ? () => nav(back) : null;
  const offlineBlocked = !online && onlineOnlyViews.has(view);

  return (
    <SecondaryBackContext.Provider value={onBack}>
      <ScreenErrorBoundary offline={!online} key={view}>
        {offlineBlocked ? (
          <NeedsConnection
            detail="This section needs a connection to load its data. Reconnect and try again."
          />
        ) : (
          <>
            {view === "achievements" && (
              <Achievements
                data={data}
                today={today}
                priceCount={priceRows.length}
                onAction={(action) => {
                  if (action === "reconcile") { onOpenReconciliation(); return; }
                  if (action === "budget") { nav("budgets"); return; }
                  if (action === "categorize") { onStartCategorizeReview(); return; }
                  if (action === "transactions") { nav("transactions"); return; }
                  if (action === "accounts") nav("accounts");
                }}
              />
            )}
            {view === "prices" && (
              <PriceHistory
                transactions={data.transactions || []}
                payees={data.payees || []}
                onChanged={setPriceRows}
                onOpenTransaction={(transaction) => setDetail(transaction)}
              />
            )}
            {view === "account-settings" && (
              <AccountSettings data={data} prices={priceRows} profile={profile} onSaved={() => refresh(true)} />
            )}
            {view === "profile-settings" && (
              <ProfileSettings data={data} profile={profile} onSaved={() => refresh(true)} onSwitchProfile={onSwitchProfile} />
            )}
            {view === "payees" && (
              <Payees data={data} ensure={ensure} onSaved={() => refresh(true)} onOpenTransaction={setDetail} onOpenIou={(id) => { setIouFocus(id); nav("ious"); }} />
            )}
            {view === "ious" && (
              <IOUs data={data} ensure={ensure} profile={profile} onSaved={() => refresh(true)} onOpenTransaction={setDetail} focusId={iouFocus} onFocusHandled={() => setIouFocus(null)} />
            )}
            {view === "bills" && (
              <Bills data={data} ensure={ensure} profile={profile} onSaved={() => refresh(true)} onOpenTransaction={setDetail} focusId={billFocus} onFocusHandled={() => setBillFocus(null)} />
            )}
            {view === "receipts" && (
              <Receipts transactions={data.transactions || []} pin={pin} onOpenTransaction={setDetail} />
            )}
            {view === "ai" && (
              <AiSettings timezone={data.timezone} />
            )}
            {view === "smart-rules" && (
              <SmartRules
                data={data}
                onSaved={() => refresh(true)}
                onOpenTransaction={setDetail}
              />
            )}
            {view === "backups" && (
              <Backups timezone={data.timezone} />
            )}
            {view === "staging-data" && (data.environment === "staging" ? <StagingData timezone={data.timezone} canReset={Boolean(data.profiles?.find(person => person.name === profile)?.owner)} /> : <p className="muted">Demo data is available in staging.</p>)}
            {view === "errors" && (
              <ErrorLogs />
            )}
            {view === "categories" && (
              <CategorySettings
                onDirtyChange={setSettingsDirty}
                data={data}
                ensure={ensure}
                onSaved={onCategoriesSaved}
              />
            )}
            {view === "budgets" && (
              <BudgetSettings data={data} ensure={ensure} profile={profile} onOpenTrip={(id) => { setTripFocus?.(id); nav("trips"); }} />
            )}
            {view === "trips" && (
              <Trips
                data={data}
                profile={profile}
                onSaved={() => refresh(true)}
                onOpenTransaction={setDetail}
                onAddTransaction={onAddTransaction}
                focusTripId={tripFocus}
                onFocusHandled={() => setTripFocus?.(null)}
              />
            )}
            {view === "goals" && (
              <Goals
                data={data}
                profile={profile}
                onSaved={() => refresh(true)}
                onOpenTransaction={setDetail}
                focusGoalId={goalFocus}
                onFocusHandled={() => setGoalFocus?.(null)}
              />
            )}
          </>
        )}
      </ScreenErrorBoundary>
    </SecondaryBackContext.Provider>
  );
}
